import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CASE_STUDIES } from "@/lib/data";

export default function CaseStudy() {
  const { slug } = useParams();
  const cs = CASE_STUDIES.find((c) => c.slug === slug);
  if (!cs) return <Navigate to="/work" replace />;
  const next = CASE_STUDIES[(CASE_STUDIES.findIndex((c) => c.slug === slug) + 1) % CASE_STUDIES.length];

  return (
    <div data-testid={`case-study-${cs.slug}`} className="bg-bone">
      {/* HERO */}
      <section className="pt-20 md:pt-28 pb-16 border-b border-border">
        <div className="tr-container grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-8">
            <Link
              to="/work"
              data-testid="case-back"
              className="inline-flex items-center gap-2 text-sm link-underline mb-10 text-ink/60 hover:text-ink"
            >
              <ArrowLeft className="w-4 h-4" /> All work
            </Link>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.22em] text-ink/50 mb-8">
              <span>{cs.sector}</span>
              <span className="hidden md:inline">·</span>
              <span>{cs.year}</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-[88px] leading-[0.98] tracking-tight">
              {cs.name}
            </h1>
            <p className="mt-8 text-xl md:text-2xl text-ink/70 leading-relaxed max-w-3xl font-serif italic">
              {cs.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* HERO IMAGE */}
      <section className="py-12">
        <div className="tr-container">
          <div className="aspect-[16/8] overflow-hidden">
            <img
              src={cs.cover_image}
              alt={cs.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="py-16 border-y border-border">
        <div className="tr-container">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {cs.metrics.map((m, i) => (
              <Reveal
                key={m.label}
                delay={i}
                className={`p-6 md:p-10 ${i !== 3 ? "md:border-r border-border" : ""} ${
                  i < 2 ? "border-b md:border-b-0 border-border" : ""
                }`}
              >
                <div className="display-num text-4xl md:text-6xl text-ink">
                  {m.value}
                </div>
                <div className="mt-3 text-xs uppercase tracking-[0.18em] text-ink/50 max-w-[26ch] leading-relaxed">
                  {m.label}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CHALLENGE / APPROACH / OUTCOMES */}
      <section className="py-24 md:py-32">
        <div className="tr-container grid grid-cols-12 gap-10 md:gap-16">
          <Reveal className="col-span-12 md:col-span-4">
            <div className="overline text-vermilion sticky top-24">The challenge</div>
          </Reveal>
          <Reveal delay={1} className="col-span-12 md:col-span-8">
            <p className="font-serif text-2xl md:text-3xl leading-[1.3] tracking-tight text-ink">
              {cs.challenge}
            </p>
          </Reveal>
        </div>

        <div className="tr-container grid grid-cols-12 gap-10 md:gap-16 mt-20 md:mt-28">
          <Reveal className="col-span-12 md:col-span-4">
            <div className="overline text-vermilion sticky top-24">Our approach</div>
          </Reveal>
          <Reveal delay={1} className="col-span-12 md:col-span-8">
            <p className="text-lg text-ink/80 leading-relaxed">{cs.approach}</p>
          </Reveal>
        </div>

        <div className="tr-container grid grid-cols-12 gap-10 md:gap-16 mt-20 md:mt-28">
          <Reveal className="col-span-12 md:col-span-4">
            <div className="overline text-vermilion sticky top-24">Outcomes</div>
          </Reveal>
          <div className="col-span-12 md:col-span-8">
            <ul className="divide-y divide-border border-t border-b border-border">
              {cs.outcomes.map((o, i) => (
                <Reveal key={i} delay={i} as="li" className="py-6 flex gap-6">
                  <span className="font-mono text-xs text-vermilion pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-lg text-ink leading-relaxed">{o}</p>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHY US + STACK */}
      <section className="py-24 md:py-32 bg-ink text-white">
        <div className="tr-container grid grid-cols-12 gap-10 md:gap-16">
          <div className="col-span-12 md:col-span-7">
            <div className="overline text-vermilion mb-6">Why they work with us</div>
            <p className="font-serif text-2xl md:text-3xl leading-[1.25] tracking-tight">
              {cs.why_us}
            </p>

            <div className="mt-14 pt-10 border-t border-white/15">
              <p className="font-serif italic text-xl md:text-2xl leading-relaxed text-white/90">
                “{cs.quote}”
              </p>
              <div className="mt-6 text-xs uppercase tracking-[0.22em] text-white/60">
                {cs.quote_attrib}
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-5 md:pl-10 md:border-l border-white/15">
            <div className="overline text-vermilion mb-6">Technology</div>
            <ul className="space-y-3">
              {cs.stack.map((s) => (
                <li
                  key={s}
                  className="text-sm text-white/80 border-b border-white/15 py-3"
                >
                  {s}
                </li>
              ))}
            </ul>

            <Link
              to="/contact"
              data-testid="case-cta-similar"
              className="mt-12 inline-flex items-center gap-2 border border-white px-7 py-4 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-white hover:text-ink transition-colors"
            >
              Build something similar <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* NEXT */}
      <section className="py-24 md:py-32 border-t border-border">
        <div className="tr-container">
          <Link
            to={`/work/${next.slug}`}
            data-testid="case-next"
            className="block group"
          >
            <div className="overline text-vermilion mb-6">Next case study</div>
            <div className="grid grid-cols-12 gap-8 items-center">
              <div className="col-span-12 md:col-span-4 aspect-[4/3] overflow-hidden">
                <img
                  src={next.hero_image}
                  alt={next.name}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                />
              </div>
              <div className="col-span-12 md:col-span-8">
                <h3 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight group-hover:text-vermilion transition-colors">
                  {next.name} →
                </h3>
                <p className="mt-4 text-lg text-ink/70">{next.tagline}</p>
              </div>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
